import { domainCodeAtom, SingleAutocomplete } from '@australiangreens/ag-internal-components';
import SearchIcon from '@mui/icons-material/Search';
import { AutocompleteChangeReason, SxProps, Theme, Tooltip } from '@mui/material';

import { getCatCampaignsContaining } from '@/apis/catcampaigns';
import { CatCampaignRecord } from '@/types';
import { useAuth0 } from '@auth0/auth0-react';
import { labelSorting } from '@/util/labelSorting';
import { useAtomValue } from 'jotai';
import { ReactNode, SyntheticEvent, useCallback } from 'react';

const DEFAULT_LABEL = 'Select a campaign';

const DEFAULT_HELPER_TEXT = (
  <Tooltip
    title={
      <>
        <p>Campaigns are set up for elections, issues, party bodies, and elected reps.</p>
        <p>Talk to your state or territory office if you are unsure which campaign to select.</p>
      </>
    }
    arrow
    // placement="right"
  >
    <span>Unsure which campaign to select?</span>
  </Tooltip>
);
type CampaignAutocompleteProps = {
  value: CatCampaignRecord | null;
  onChange: (
    newValue: CatCampaignRecord | null,
    reason: AutocompleteChangeReason, // Might only be a subset of this
    event: SyntheticEvent<Element, Event>
  ) => unknown;

  sx?: SxProps<Theme>;
  label?: string;
  disabled?: boolean;
  helperText?: ReactNode;
  error?: boolean;
};

export const useLookupCatCampaigns = () => {
  const { getAccessTokenSilently } = useAuth0();
  const domainCode = useAtomValue(domainCodeAtom);

  return useCallback(
    async (lookupValue: string) => {
      const token = await getAccessTokenSilently();

      return (await getCatCampaignsContaining(token, lookupValue, domainCode)).sort(labelSorting);
    },
    [domainCode, getAccessTokenSilently]
  );
};

export default function CampaignAutocomplete({
  value,
  label = DEFAULT_LABEL,
  sx,
  onChange,
  disabled,
  helperText = DEFAULT_HELPER_TEXT,
  error = false,
}: CampaignAutocompleteProps) {
  const lookupCampaigns = useLookupCatCampaigns();

  return (
    <SingleAutocomplete
      value={value}
      label={label}
      lookup={lookupCampaigns}
      onChange={(event, newValue, reason) => onChange(newValue, reason, event)}
      sx={sx}
      hideButton
      popupIcon={<SearchIcon />}
      disableIconFlip
      disabled={disabled}
      error={error}
      helperText={helperText}
    />
  );
}
